// ANANT SMART HEALTH TRACKER — Cinematic 0-cost Edition
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Heart, Activity, Camera, FileText, Scale, FolderLock, Loader2, Sparkles,
  Play, Square, Users, TrendingUp, Award, Copy, Download, Utensils, Zap,
  Calendar, Plus, Trash2, Edit3, FileDown,
} from 'lucide-react';
import ATWalletBadge from '../../components/ATWalletBadge';
import VoiceInputHelper, { parseNumericFields } from '../../components/VoiceInputHelper';
import api from '../../utils/api';

// ---- Shared file upload helper ----
async function uploadImageFile(file) {
  const form = new FormData();
  form.append('file', file);
  const token = localStorage.getItem('token');
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
    method: 'POST', body: form, headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.url.startsWith('http') ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}

export default function AnantHealthTracker() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/health/config'); setCfg(r.data); }
    catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !cfg) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const u = cfg.user || {};
  const active = u.is_active;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-rose-950 via-slate-950 to-emerald-950 text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-rose-400/60 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white">← Home</Link>
        <ATWalletBadge compact />
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* HERO */}
        <div className="relative overflow-hidden rounded-3xl border-2 border-rose-400/50 bg-gradient-to-br from-rose-950 via-fuchsia-900/40 to-emerald-950 p-5 mb-6" data-testid="ht-hero">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none animate-pulse"></div>
          <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-rose-500/25 blur-3xl pointer-events-none"></div>
          <div className="relative flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-2 border-rose-400 shadow-xl shadow-rose-500/50 flex items-center justify-center bg-gradient-to-br from-rose-500 to-emerald-500">
              <Heart className="w-10 h-10 text-white animate-pulse" fill="white" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/25 border border-rose-400/40 text-[9px] font-black tracking-widest text-rose-200 mb-1">
                <Sparkles className="w-2.5 h-2.5" /> AI · CINEMATIC · 0-COST
              </div>
              <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-rose-300 via-fuchsia-200 to-emerald-300 bg-clip-text text-transparent" style={{ fontFamily: '"Cinzel", Georgia, serif' }}>
                ANANT SMART HEALTH TRACKER
              </h1>
              <div className="text-xs opacity-90 mt-0.5">Your Health · AI-Powered · Camera-Based · 7-Level Cashback</div>
            </div>
          </div>
          <div className="mt-3 flex gap-1.5 flex-wrap">
            <Chip color="rose">❤️ Camera PPG Pulse</Chip>
            <Chip color="emerald">🥗 AI Food Scanner</Chip>
            <Chip color="amber">📊 BMI Portal</Chip>
            <Chip color="blue">📄 Medical OCR Locker</Chip>
            <Chip color="fuchsia">🎯 33% · 7-Level Cascade</Chip>
          </div>
        </div>

        {/* Sub Status Banner */}
        <SubBanner cfg={cfg} onReload={load} refCode={refCode} />

        {/* Tabs */}
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 p-1 rounded-2xl border border-slate-700 bg-slate-900/70 mb-4" data-testid="ht-tabs">
          <TabBtn active={tab === 'dashboard'} onClick={() => setTab('dashboard')} data-testid="ht-tab-dashboard"><TrendingUp className="w-3 h-3" /> Dashboard</TabBtn>
          <TabBtn active={tab === 'daily'} onClick={() => setTab('daily')} data-testid="ht-tab-daily"><Calendar className="w-3 h-3" /> Daily Log</TabBtn>
          <TabBtn active={tab === 'pulse'} onClick={() => setTab('pulse')} data-testid="ht-tab-pulse"><Heart className="w-3 h-3" /> Pulse</TabBtn>
          <TabBtn active={tab === 'motion'} onClick={() => setTab('motion')} data-testid="ht-tab-motion"><Activity className="w-3 h-3" /> Motion</TabBtn>
          <TabBtn active={tab === 'food'} onClick={() => setTab('food')} data-testid="ht-tab-food"><Utensils className="w-3 h-3" /> Food AI</TabBtn>
          <TabBtn active={tab === 'bmi'} onClick={() => setTab('bmi')} data-testid="ht-tab-bmi"><Scale className="w-3 h-3" /> BMI</TabBtn>
          <TabBtn active={tab === 'locker'} onClick={() => setTab('locker')} data-testid="ht-tab-locker"><FolderLock className="w-3 h-3" /> Locker</TabBtn>
          <TabBtn active={tab === 'aireport'} onClick={() => setTab('aireport')} data-testid="ht-tab-aireport"><Sparkles className="w-3 h-3" /> AI Report</TabBtn>
        </div>

        {tab === 'dashboard' && <DashboardTab cfg={cfg} />}
        {tab === 'daily' && <DailyLogTab active={active} />}
        {tab === 'pulse' && <PulseVisionTab />}
        {tab === 'motion' && <MotionMatrixTab />}
        {tab === 'food' && <FoodScannerTab active={active} />}
        {tab === 'bmi' && <BMIPortalTab cfg={cfg} active={active} />}
        {tab === 'locker' && <MedicalLockerTab active={active} />}
        {tab === 'aireport' && <AIReportTab active={active} />}

        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-[10px] text-slate-400 leading-relaxed">
          <b className="text-slate-200">Health Tracker Disclaimer:</b> All readings from Camera PPG, DeviceMotion sensors, AI food scans, and medical OCR are for informational/lifestyle purposes only and do NOT replace professional medical diagnosis. Always consult a qualified doctor for medical decisions.
        </div>
      </div>
    </div>
  );
}

const Chip = ({ children, color = 'rose' }) => {
  const map = {
    rose: 'bg-rose-500/15 text-rose-200 border-rose-400/30',
    emerald: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30',
    amber: 'bg-amber-500/15 text-amber-200 border-amber-400/30',
    blue: 'bg-blue-500/15 text-blue-200 border-blue-400/30',
    fuchsia: 'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30',
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-black ${map[color]}`}>{children}</span>;
};

const TabBtn = ({ active, onClick, children, ...rest }) => (
  <button onClick={onClick} {...rest} className={`px-2 py-2 rounded-xl text-[11px] font-black transition-all flex items-center justify-center gap-1 ${active ? 'bg-gradient-to-r from-rose-500 to-emerald-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>{children}</button>
);

// ============ SUBSCRIPTION BANNER ============
function SubBanner({ cfg, onReload, refCode }) {
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState('monthly');
  const [accept, setAccept] = useState(false);
  const [refInput, setRefInput] = useState(refCode || cfg.user?.referred_by_username || '');
  const u = cfg.user || {};
  const active = u.is_active;
  const exp = u.subscription?.expiry_at;

  const subscribe = async () => {
    if (!accept) { toast.error('T&C accept karo'); return; }
    setBusy(true);
    try {
      const r = await api.post('/health/subscribe', { plan: selected, referral_code: refInput || undefined, accept_terms: true });
      toast.success(r.data.message);
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (active) {
    return (
      <div className="rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-950/40 to-slate-900 p-3 mb-4 flex items-center gap-3" data-testid="ht-sub-active">
        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-black" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">✨ Active Subscriber</div>
          <div className="text-sm font-black">Valid till: <span className="text-emerald-300">{new Date(exp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
        </div>
        <button onClick={() => { setSelected('monthly'); setBusy(false); document.getElementById('renew-hint')?.scrollIntoView(); }} className="text-[10px] px-2 py-1 rounded bg-emerald-500 text-black font-black">Renew</button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 to-slate-900 p-4 mb-4" data-testid="ht-sub-locked">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">🔒 Subscribe to Unlock BMI Portal · Food AI · Medical Locker</div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        {Object.values(cfg.plans).map((p) => (
          <button key={p.code} onClick={() => setSelected(p.code)} data-testid={`ht-plan-${p.code}`}
            className={`text-left rounded-xl border-2 p-3 transition-all ${selected === p.code ? 'border-amber-400 bg-amber-500/15 shadow-lg shadow-amber-500/40' : 'border-slate-700 bg-slate-800/40 hover:border-amber-400/50'}`}>
            <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black truncate">{p.label}</div>
            <div className="text-base sm:text-lg font-black">{p.price_at.toLocaleString()} AT</div>
            <div className="text-[9px] opacity-70">{p.days}d · {p.seats > 1 ? `${p.seats} members` : '33% cashback'}</div>
          </button>
        ))}
      </div>
      <input placeholder="Referral code (sponsor username, optional)" value={refInput} onChange={(e) => setRefInput(e.target.value)} data-testid="ht-ref-input" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2" />
      <label className="flex items-start gap-2 text-[10px] cursor-pointer mb-2">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="ht-accept" className="mt-0.5" />
        <span>I accept Health Tracker Terms — subscription non-refundable, distributed via 33% 7-level cascade + admin.</span>
      </label>
      <button onClick={subscribe} disabled={busy || !accept} data-testid="ht-subscribe" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-rose-500 via-fuchsia-500 to-emerald-500 text-white text-sm font-black disabled:opacity-40">
        {busy ? 'Processing…' : `Pay ${cfg.plans[selected]?.price_at} AT · Activate`}
      </button>
    </div>
  );
}

// ============ DASHBOARD ============
function DashboardTab({ cfg }) {
  const [pulse, setPulse] = useState([]);
  const [motion, setMotion] = useState([]);
  const [food, setFood] = useState({ logs: [], total_calories_today: 0 });
  const [bmi, setBmi] = useState([]);
  const [refData, setRefData] = useState(null);
  const u = cfg.user || {};
  useEffect(() => {
    Promise.all([
      api.get('/health/pulse/history', { params: { limit: 7 } }),
      api.get('/health/motion/history', { params: { limit: 7 } }),
      api.get('/health/food/history', { params: { limit: 5 } }),
      api.get('/health/bmi/history', { params: { limit: 5 } }),
      api.get('/health/referral/earnings', { params: { days: 30 } }),
    ]).then(([p, m, f, b, r]) => {
      setPulse(p.data?.logs || []);
      setMotion(m.data?.logs || []);
      setFood(f.data || {});
      setBmi(b.data?.logs || []);
      setRefData(r.data || null);
    }).catch(() => {});
  }, []);

  const lastPulse = pulse[0];
  const lastBmi = bmi[0];
  const todaySteps = motion.filter((x) => x.date === new Date().toISOString().slice(0, 10)).reduce((s, x) => s + (x.steps || 0), 0);
  const refLink = `${window.location.origin}/health-tracker?ref=${u.referral_code || u.username}`;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="❤️ Last BPM" value={lastPulse ? lastPulse.bpm : '—'} sub={lastPulse?.category || 'No reading'} color="rose" />
        <StatTile label="🚶 Steps Today" value={todaySteps.toLocaleString()} sub={`${motion.length}d tracked`} color="emerald" />
        <StatTile label="⚖️ Last BMI" value={lastBmi ? lastBmi.bmi : '—'} sub={lastBmi?.category || 'Calc pending'} color="amber" />
        <StatTile label="🥗 Calories Today" value={Math.round(food.total_calories_today || 0)} sub={`${food.logs?.length || 0} meals`} color="blue" />
      </div>

      {/* Referral / Cascade card */}
      {refData && (
        <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-950/40 to-slate-900 p-4" data-testid="ht-referral">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black flex items-center gap-1"><Award className="w-3 h-3" /> 7-Level Cashback (33% Pool)</div>
            <div className="text-2xl font-black bg-gradient-to-r from-fuchsia-300 to-emerald-300 bg-clip-text text-transparent">{refData.total_at.toLocaleString()} AT</div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-[9px] text-center mb-2">
            {cfg.cascade_levels.map((l) => {
              const earned = refData.level_totals?.[`L${l.level}`] || 0;
              return (
                <div key={l.level} className="rounded p-1 border border-slate-700 bg-slate-800/40">
                  <div className="font-black text-fuchsia-300">L{l.level}</div>
                  <div className="opacity-80">{l.pct}%</div>
                  <div className="text-[8px] text-emerald-300 mt-0.5">{earned > 0 ? `+${earned}` : '—'}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-[10px]">
            <Users className="w-3 h-3" />
            <span>Directs: <b className="text-emerald-300">{refData.active_directs}</b>/{refData.directs} active</span>
            <div className="ml-auto flex items-center gap-1">
              <code className="text-[9px] px-2 py-1 rounded bg-slate-800 text-amber-200 truncate max-w-[200px]">{refLink}</code>
              <button onClick={() => { navigator.clipboard.writeText(refLink); toast.success('Copied'); }} data-testid="ht-ref-copy" className="px-1.5 py-1 rounded bg-fuchsia-500 text-white"><Copy className="w-3 h-3" /></button>
            </div>
          </div>
        </div>
      )}

      <FamilyPlanCard cfg={cfg} />
      <ProfileEditor cfg={cfg} />
      <WeeklyReportCard />
      <DoctorMarketplaceCard active={cfg.user?.is_active} />
    </div>
  );
}

// ============ PROFILE EDITOR ============
function ProfileEditor({ cfg }) {
  const [p, setP] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { setP(cfg?.user?.profile || {}); }, [cfg]);
  const save = async () => {
    setBusy(true);
    try { const r = await api.post('/health/profile', p); toast.success('Profile saved'); setP(r.data.profile); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const set = (k, v) => setP({ ...p, [k]: v === '' ? null : v });
  const bp = p.blood_pressure_sys && p.blood_pressure_dia ? `${p.blood_pressure_sys}/${p.blood_pressure_dia}` : '—';
  return (
    <div className="rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-950/40 to-slate-900 p-3" data-testid="ht-profile">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black flex items-center gap-1">👤 Health Profile</div>
          <div className="text-[10px] opacity-80 mt-0.5">Age {p.age || '—'} · {p.blood_group || 'BG—'} · BP {bp} · Sugar F/PP {p.sugar_fasting || '—'}/{p.sugar_pp || '—'} · Activity {p.daily_activity_level || '—'}</div>
        </div>
        <span className="text-[10px] opacity-70">{open ? '▲' : '▼ Edit'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <NumInput label="Age" v={p.age} on={(v) => set('age', parseInt(v) || null)} tid="ht-p-age" />
            <SelInput label="Gender" v={p.gender} on={(v) => set('gender', v)} opts={['', 'M', 'F', 'O']} tid="ht-p-gen" />
            <SelInput label="Blood Group" v={p.blood_group} on={(v) => set('blood_group', v)} opts={['', 'A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']} tid="ht-p-bg" />
            <NumInput label="Weight (kg)" v={p.weight_kg} on={(v) => set('weight_kg', parseFloat(v) || null)} tid="ht-p-wt" />
            <NumInput label="Height (cm)" v={p.height_cm} on={(v) => set('height_cm', parseFloat(v) || null)} tid="ht-p-ht" />
            <NumInput label="BP Systolic" v={p.blood_pressure_sys} on={(v) => set('blood_pressure_sys', parseInt(v) || null)} tid="ht-p-bps" />
            <NumInput label="BP Diastolic" v={p.blood_pressure_dia} on={(v) => set('blood_pressure_dia', parseInt(v) || null)} tid="ht-p-bpd" />
            <NumInput label="Sugar Fasting" v={p.sugar_fasting} on={(v) => set('sugar_fasting', parseFloat(v) || null)} tid="ht-p-sf" />
            <NumInput label="Sugar PP" v={p.sugar_pp} on={(v) => set('sugar_pp', parseFloat(v) || null)} tid="ht-p-spp" />
            <SelInput label="Activity Level" v={p.daily_activity_level} on={(v) => set('daily_activity_level', v)} opts={['', 'Sedentary', 'Lightly Active', 'Moderately Active', 'Very Active', 'Athlete']} tid="ht-p-act" />
            <NumInput label="Water (glasses/day)" v={p.daily_water_glasses} on={(v) => set('daily_water_glasses', parseInt(v) || null)} tid="ht-p-water" />
            <NumInput label="Sleep (hrs/day)" v={p.daily_sleep_hours} on={(v) => set('daily_sleep_hours', parseFloat(v) || null)} tid="ht-p-sleep" />
          </div>
          <textarea placeholder="Allergies (peanuts, dust, penicillin…)" value={p.allergies || ''} onChange={(e) => set('allergies', e.target.value)} data-testid="ht-p-allergies" rows={2} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <textarea placeholder="Chronic Conditions (diabetes, hypertension, asthma…)" value={p.chronic_conditions || ''} onChange={(e) => set('chronic_conditions', e.target.value)} data-testid="ht-p-chronic" rows={2} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <textarea placeholder="Regular Medications" value={p.medications || ''} onChange={(e) => set('medications', e.target.value)} data-testid="ht-p-meds" rows={2} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Emergency Contact (phone)" value={p.emergency_contact || ''} onChange={(e) => set('emergency_contact', e.target.value)} data-testid="ht-p-emrg" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={save} disabled={busy} data-testid="ht-p-save" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-sm font-black disabled:opacity-40">{busy ? 'Saving…' : '💾 Save Profile'}</button>
        </div>
      )}
    </div>
  );
}

const NumInput = ({ label, v, on, tid }) => (
  <label className="text-[10px]"><span className="opacity-80 block mb-0.5">{label}</span>
    <input type="number" value={v ?? ''} onChange={(e) => on(e.target.value)} data-testid={tid} className="w-full px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
  </label>
);
const SelInput = ({ label, v, on, opts, tid }) => (
  <label className="text-[10px]"><span className="opacity-80 block mb-0.5">{label}</span>
    <select value={v || ''} onChange={(e) => on(e.target.value)} data-testid={tid} className="w-full px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
      {opts.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
    </select>
  </label>
);

// ============ FAMILY PLAN CARD ============
function FamilyPlanCard({ cfg }) {
  const [data, setData] = useState(null);
  const [uname, setUname] = useState('');
  const [busy, setBusy] = useState(false);
  const isFamilyOwner = cfg?.user?.subscription?.plan_type === 'family';
  const load = () => api.get('/health/family').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!uname.trim()) return;
    setBusy(true);
    try { await api.post('/health/family/add', { username: uname.trim() }); toast.success('Member added'); setUname(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const remove = async (uid) => {
    if (!window.confirm('Remove member?')) return;
    try { await api.delete(`/health/family/remove/${uid}`); load(); } catch { toast.error('Failed'); }
  };
  if (!data) return null;
  // Show member-of hint even if not owner
  if (!isFamilyOwner && !data.member_of) {
    return (
      <div className="rounded-2xl border border-teal-400/30 bg-teal-500/5 p-3" data-testid="ht-family-hint">
        <div className="text-[10px] uppercase tracking-widest text-teal-300 font-black mb-1">👨‍👩‍👧‍👦 Family Wellness Plan · Available</div>
        <div className="text-[11px] opacity-90">Subscribe to <b>Family Plan (2000 AT · 5 members)</b> to share your subscription with parents, spouse & kids — sabko premium access!</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border-2 border-teal-400/40 bg-gradient-to-br from-teal-950/40 to-slate-900 p-3" data-testid="ht-family">
      <div className="text-[10px] uppercase tracking-widest text-teal-300 font-black mb-2 flex items-center justify-between">
        <span>👨‍👩‍👧‍👦 {isFamilyOwner ? 'My Family Plan' : 'Your Family'}</span>
        {isFamilyOwner && <span className="text-[9px] opacity-80">{(data.members || []).length}/5</span>}
      </div>
      {data.member_of && !isFamilyOwner && (
        <div className="text-[11px] bg-emerald-500/15 border border-emerald-400/30 rounded p-2 mb-2">✨ Included in <b>@{data.member_of.username}</b>&apos;s family plan · premium unlocked</div>
      )}
      {isFamilyOwner && (
        <>
          <div className="space-y-1 max-h-40 overflow-y-auto mb-2">
            {(data.members || []).length === 0 ? (
              <div className="text-[11px] opacity-70 text-center py-2">No members yet · add up to 5</div>
            ) : data.members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-2 rounded bg-slate-800/60 px-2 py-1 text-[11px]" data-testid={`ht-fam-${m.user_id}`}>
                <span className="w-6 h-6 rounded-full bg-teal-500/25 text-teal-200 flex items-center justify-center text-[10px] font-black">{(m.username || '?')[0]?.toUpperCase()}</span>
                <div className="flex-1 truncate"><b>{m.full_name || m.username}</b> <span className="opacity-60">@{m.username}</span></div>
                <button onClick={() => remove(m.user_id)} className="text-red-300 text-xs">✕</button>
              </div>
            ))}
          </div>
          {(data.members || []).length < 5 && (
            <div className="flex gap-2">
              <input placeholder="Username to add" value={uname} onChange={(e) => setUname(e.target.value)} data-testid="ht-fam-input" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <button onClick={add} disabled={busy} data-testid="ht-fam-add" className="px-3 py-1.5 rounded bg-teal-500 text-black text-xs font-black">+ Add</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============ WEEKLY REPORT CARD ============
function WeeklyReportCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/health/weekly-report', { params: { days: 7 } }).then((r) => setData(r.data)).catch(() => {}); }, []);
  const downloadPdf = async () => {
    if (!data) return;
    setBusy(true);
    try {
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();
      let y = 40;
      doc.setFillColor(190, 24, 93); doc.rect(0, 0, W, 90, 'F');
      doc.setTextColor(255, 240, 200); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
      doc.text('ANANT SMART HEALTH TRACKER', W / 2, 35, { align: 'center' });
      doc.setFontSize(11); doc.text('AI Weekly Health Report (7 Days)', W / 2, 60, { align: 'center' });
      y = 120;
      doc.setTextColor(20, 20, 20); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text(`User: @${data.user.username} · Generated: ${new Date().toLocaleString('en-IN')}`, 40, y); y += 24;

      // Score card
      const scoreColor = data.overall_health_score >= 80 ? [16, 185, 129] : data.overall_health_score >= 60 ? [59, 130, 246] : data.overall_health_score >= 40 ? [245, 158, 11] : [239, 68, 68];
      doc.setFillColor(...scoreColor); doc.rect(40, y, W - 80, 60, 'F');
      doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(28);
      doc.text(`${data.overall_health_score}/100 · ${data.verdict}`, W / 2, y + 40, { align: 'center' });
      y += 90;

      // Sections
      const rows = [
        ['❤️ Pulse Readings', `${data.pulse.count} readings · Avg ${data.pulse.avg_bpm} BPM (min ${data.pulse.min_bpm} · max ${data.pulse.max_bpm})`],
        ['🚶 Motion', `${data.motion.total_steps.toLocaleString()} steps · ${data.motion.total_km} km · ${data.motion.total_calories} kcal (${data.motion.count} sessions)`],
        ['🥗 Nutrition', `${data.food.count} meals scanned · ${data.food.total_calories.toLocaleString()} kcal total · avg health ${data.food.avg_health_score}/100`],
        ['⚖️ BMI', data.bmi.latest ? `Latest: ${data.bmi.latest.bmi} (${data.bmi.latest.category})` : 'Not measured'],
      ];
      doc.setTextColor(20); doc.setFontSize(11);
      rows.forEach(([label, val]) => {
        doc.setFont('helvetica', 'bold'); doc.text(label, 40, y); y += 14;
        doc.setFont('helvetica', 'normal'); doc.text(val, 50, y); y += 22;
      });

      doc.setFontSize(8); doc.setTextColor(120);
      doc.text('This is an auto-generated summary. Consult a qualified doctor for medical decisions.', W / 2, 800, { align: 'center' });
      doc.save(`ASN-Health-Weekly-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) { toast.error('PDF failed'); }
    finally { setBusy(false); }
  };
  const shareWa = () => {
    if (!data) return;
    const msg = [
      `❤️ *Anant Smart Health · Weekly Report*`,
      `User: @${data.user.username}`,
      ``,
      `🏆 *Health Score: ${data.overall_health_score}/100 · ${data.verdict}*`,
      ``,
      `❤️ Pulse: ${data.pulse.count} readings · Avg ${data.pulse.avg_bpm} BPM`,
      `🚶 Motion: ${data.motion.total_steps.toLocaleString()} steps · ${data.motion.total_km}km · ${data.motion.total_calories}kcal`,
      `🥗 Nutrition: ${data.food.count} meals · ${data.food.total_calories.toLocaleString()} kcal · Avg score ${data.food.avg_health_score}/100`,
      `⚖️ BMI: ${data.bmi.latest ? `${data.bmi.latest.bmi} (${data.bmi.latest.category})` : '—'}`,
      ``,
      `Anant Smart Health Tracker`,
    ].join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };
  if (!data) return null;
  const scoreColor = data.overall_health_score >= 80 ? 'text-emerald-300' : data.overall_health_score >= 60 ? 'text-blue-300' : data.overall_health_score >= 40 ? 'text-amber-300' : 'text-red-300';
  return (
    <div className="rounded-2xl border-2 border-rose-400/40 bg-gradient-to-br from-rose-950/40 via-fuchsia-900/20 to-slate-900 p-3" data-testid="ht-weekly">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black flex items-center gap-1"><FileText className="w-3 h-3" /> AI Weekly Health Report (7 days)</div>
        <div className={`text-2xl font-black ${scoreColor}`}>{data.overall_health_score}<span className="text-xs opacity-70">/100</span></div>
      </div>
      <div className="text-[11px] font-black text-center mb-2">{data.verdict}</div>
      <div className="grid grid-cols-4 gap-1 text-center text-[10px] mb-2">
        <div className="rounded bg-rose-500/15 border border-rose-400/30 p-1.5"><div className="opacity-70">❤️ BPM</div><b className="text-rose-200">{data.pulse.avg_bpm || '—'}</b></div>
        <div className="rounded bg-emerald-500/15 border border-emerald-400/30 p-1.5"><div className="opacity-70">🚶 Steps</div><b className="text-emerald-200">{data.motion.total_steps.toLocaleString()}</b></div>
        <div className="rounded bg-blue-500/15 border border-blue-400/30 p-1.5"><div className="opacity-70">🥗 Cals</div><b className="text-blue-200">{Math.round(data.food.total_calories).toLocaleString()}</b></div>
        <div className="rounded bg-amber-500/15 border border-amber-400/30 p-1.5"><div className="opacity-70">⚖️ BMI</div><b className="text-amber-200">{data.bmi.latest?.bmi || '—'}</b></div>
      </div>
      <div className="flex gap-2">
        <button onClick={downloadPdf} disabled={busy} data-testid="ht-weekly-pdf" className="flex-1 px-2 py-1.5 rounded bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-xs font-black flex items-center justify-center gap-1"><Download className="w-3 h-3" /> {busy ? '…' : 'Download PDF'}</button>
        <button onClick={shareWa} data-testid="ht-weekly-wa" className="flex-1 px-2 py-1.5 rounded bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black">💬 WhatsApp</button>
      </div>
    </div>
  );
}

// ============ DOCTOR MARKETPLACE CARD ============
function DoctorMarketplaceCard({ active }) {
  const [doctors, setDoctors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lockerDocs, setLockerDocs] = useState([]);
  const [slot, setSlot] = useState('Today Evening');
  const [notes, setNotes] = useState('');
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/health/doctors').then((r) => setDoctors(r.data?.doctors || [])).catch(() => {});
    if (active) api.get('/health/locker').then((r) => setLockerDocs(r.data?.documents || [])).catch(() => {});
  }, [active]);
  const book = async () => {
    if (!selected) return;
    if (!active) { toast.error('Active subscription required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/health/doctors/book', { doctor_id: selected.id, preferred_slot: slot, notes, shared_doc_ids: chosen });
      toast.success('Booking created · opening WhatsApp…');
      window.open(r.data.booking.whatsapp_link, '_blank');
      setSelected(null); setChosen([]); setNotes('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border-2 border-blue-400/40 bg-gradient-to-br from-blue-950/40 to-slate-900 p-3" data-testid="ht-doctors">
      <div className="text-[10px] uppercase tracking-widest text-blue-300 font-black mb-2 flex items-center gap-1">🩺 Doctor Marketplace · Verified Consults</div>
      {doctors.length === 0 ? (
        <div className="text-[11px] opacity-70 text-center py-3">Admin ne abhi tak koi doctor list nahi kiya. Coming soon!</div>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {doctors.map((d) => (
            <button key={d.id} onClick={() => setSelected(d)} data-testid={`ht-doc-${d.id}`}
              className={`w-full text-left rounded-lg border p-2 flex items-center gap-2 transition-all ${selected?.id === d.id ? 'border-blue-300 bg-blue-500/15' : 'border-slate-700 bg-slate-800/50 hover:border-blue-400'}`}>
              {d.photo_url ? <img src={d.photo_url} alt={d.name} className="w-10 h-10 rounded-full object-cover border border-blue-400" onError={(e) => e.currentTarget.style.display = 'none'} /> : <div className="w-10 h-10 rounded-full bg-blue-500/25 flex items-center justify-center text-lg">🩺</div>}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black truncate flex items-center gap-1">{d.verified && <span className="text-[9px] px-1 rounded bg-emerald-500 text-black">✓</span>} Dr. {d.name}</div>
                <div className="text-[10px] opacity-70 truncate">{d.specialty} · {d.city} · ⭐{d.rating}</div>
              </div>
              <div className="text-right text-[11px] font-black text-amber-300">{d.fee_at} AT</div>
            </button>
          ))}
        </div>
      )}
      {selected && (
        <div className="mt-2 rounded-lg border border-blue-400/40 bg-slate-900 p-2 space-y-2" data-testid="ht-doc-form">
          <div className="text-[11px] font-black text-blue-200">Book with Dr. {selected.name}</div>
          <input placeholder="Preferred Slot (e.g. Today 6pm)" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="ht-doc-slot" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <textarea placeholder="Notes / symptoms (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ht-doc-notes" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" rows={2} />
          {lockerDocs.length > 0 && (
            <div>
              <div className="text-[10px] opacity-80 mb-1">📎 Attach medical documents (from your locker)</div>
              <div className="max-h-24 overflow-y-auto space-y-0.5">
                {lockerDocs.slice(0, 10).map((d) => (
                  <label key={d.id} className="flex items-center gap-1 text-[10px] cursor-pointer">
                    <input type="checkbox" checked={chosen.includes(d.id)} onChange={(e) => setChosen(e.target.checked ? [...chosen, d.id] : chosen.filter((x) => x !== d.id))} data-testid={`ht-doc-attach-${d.id}`} />
                    <span className="truncate flex-1">{d.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <button onClick={book} disabled={busy} data-testid="ht-doc-book" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-black disabled:opacity-40">{busy ? '⏳…' : `Book · ${selected.fee_at} AT · Open WhatsApp`}</button>
        </div>
      )}
    </div>
  );
}

const StatTile = ({ label, value, sub, color }) => {
  const map = {
    rose: 'from-rose-500/20 to-rose-900/20 border-rose-400/40',
    emerald: 'from-emerald-500/20 to-emerald-900/20 border-emerald-400/40',
    amber: 'from-amber-500/20 to-amber-900/20 border-amber-400/40',
    blue: 'from-blue-500/20 to-blue-900/20 border-blue-400/40',
  };
  return (
    <div className={`rounded-xl border p-3 bg-gradient-to-br ${map[color]}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-80 font-black">{label}</div>
      <div className="text-xl font-black">{value}</div>
      <div className="text-[10px] opacity-70">{sub}</div>
    </div>
  );
};

// ============ PULSE VISION (Camera PPG) ============
function PulseVisionTab() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [bpm, setBpm] = useState(0);
  const [progress, setProgress] = useState(0);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const stopRef = useRef(false);

  useEffect(() => {
    api.get('/health/pulse/history', { params: { limit: 15 } }).then((r) => setHistory(r.data?.logs || [])).catch(() => {});
  }, []);

  const start = async () => {
    setError(''); setBpm(0); setProgress(0); stopRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: 320, height: 240 }, audio: false });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setStreaming(true);
      // Try to enable torch (Android Chrome)
      const track = stream.getVideoTracks()[0];
      try {
        const caps = track.getCapabilities?.() || {};
        if (caps.torch) await track.applyConstraints({ advanced: [{ torch: true }] });
      } catch { /* torch unsupported */ }
      // Sample red channel intensity over 20 sec
      const samples = [];
      const startTime = Date.now();
      const durationMs = 20000;
      const ctx = canvasRef.current.getContext('2d');
      const sample = () => {
        if (stopRef.current) return;
        const elapsed = Date.now() - startTime;
        setProgress(Math.min(100, (elapsed / durationMs) * 100));
        try {
          ctx.drawImage(videoRef.current, 0, 0, 32, 24);
          const d = ctx.getImageData(0, 0, 32, 24).data;
          let sum = 0; let n = 0;
          for (let i = 0; i < d.length; i += 4) { sum += d[i]; n++; }
          const avgR = sum / n;
          samples.push({ t: elapsed, r: avgR });
        } catch { /* ignore */ }
        if (elapsed < durationMs) requestAnimationFrame(sample);
        else finish(samples, stream);
      };
      requestAnimationFrame(sample);
    } catch (e) {
      setError('Camera denied or unavailable. Finger place karo camera pe + torch on.');
      setStreaming(false);
    }
  };

  const finish = async (samples, stream) => {
    // Cleanup
    setStreaming(false);
    try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    // Detect peaks: normalise + find zero-crossings on smoothed signal
    if (samples.length < 30) { setError('Insufficient signal — retry with finger firmly on camera'); return; }
    const values = samples.map((s) => s.r);
    // Simple moving-average filter (window=5)
    const win = 5;
    const smooth = values.map((_, i) => {
      const s = values.slice(Math.max(0, i - win), Math.min(values.length, i + win + 1));
      return s.reduce((a, b) => a + b, 0) / s.length;
    });
    // Detrend: subtract long-window average
    const longWin = 20;
    const detrended = smooth.map((v, i) => {
      const s = smooth.slice(Math.max(0, i - longWin), Math.min(smooth.length, i + longWin + 1));
      const mean = s.reduce((a, b) => a + b, 0) / s.length;
      return v - mean;
    });
    // Find peaks (local maxima above small threshold)
    const std = Math.sqrt(detrended.reduce((s, v) => s + v * v, 0) / detrended.length);
    let peaks = 0;
    for (let i = 2; i < detrended.length - 2; i++) {
      if (detrended[i] > std * 0.5 && detrended[i] > detrended[i - 1] && detrended[i] > detrended[i + 1] && detrended[i] > detrended[i - 2] && detrended[i] > detrended[i + 2]) {
        peaks++;
        i += 3; // refractory
      }
    }
    const totalSec = (samples[samples.length - 1].t - samples[0].t) / 1000;
    const bpmEst = Math.round((peaks / totalSec) * 60);
    if (bpmEst < 30 || bpmEst > 220) { setError(`Signal weak (BPM=${bpmEst}). Retry — finger cover camera fully.`); return; }
    setBpm(bpmEst);
    try {
      const r = await api.post('/health/pulse/log', { bpm: bpmEst, duration_s: Math.round(totalSec), signal_quality: Math.min(100, std * 10) });
      toast.success(`💓 ${bpmEst} BPM saved — ${r.data.log?.category}`);
      setHistory((h) => [r.data.log, ...h].slice(0, 15));
    } catch (e) { toast.error('Save failed'); }
  };

  const stop = () => { stopRef.current = true; setStreaming(false); const v = videoRef.current?.srcObject; if (v) v.getTracks().forEach((t) => t.stop()); };

  return (
    <div className="space-y-3" data-testid="ht-pulse-tab">
      <div className="rounded-2xl border-2 border-rose-400/50 bg-gradient-to-br from-rose-950/60 to-slate-900 p-4 text-center">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-2 flex items-center justify-center gap-1"><Heart className="w-3 h-3 animate-pulse" fill="currentColor" /> Camera PPG · Zero-Cost Pulse Reader</div>
        <p className="text-[11px] opacity-80 mb-3">Apna finger camera par rakho + torch on karo · 20 sec measurement</p>

        <div className="relative mx-auto w-40 h-40 rounded-full border-4 border-rose-400 shadow-lg shadow-rose-500/40 overflow-hidden bg-black mb-3">
          <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />
          {!streaming && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70">
              <Heart className={`w-20 h-20 text-rose-400 ${bpm > 0 ? 'animate-pulse' : ''}`} fill={bpm > 0 ? '#f43f5e' : 'transparent'} />
              {bpm > 0 && <div className="text-2xl font-black text-white -mt-2">{bpm}<span className="text-xs">BPM</span></div>}
            </div>
          )}
          {streaming && (
            <div className="absolute inset-0 border-4 border-rose-400 rounded-full animate-ping opacity-40"></div>
          )}
        </div>
        <canvas ref={canvasRef} width={32} height={24} className="hidden" />
        {streaming && (
          <div className="mb-2">
            <div className="text-[10px] mb-1">Measuring… {Math.round(progress)}%</div>
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-rose-500 to-emerald-500 transition-all" style={{ width: `${progress}%` }}></div></div>
          </div>
        )}
        {error && <div className="text-[11px] text-red-300 mb-2">{error}</div>}
        {!streaming ? (
          <button onClick={start} data-testid="ht-pulse-start" className="px-5 py-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-black text-sm inline-flex items-center gap-1"><Play className="w-4 h-4" /> Start Measurement</button>
        ) : (
          <button onClick={stop} data-testid="ht-pulse-stop" className="px-5 py-2 rounded-full bg-red-500 text-white font-black text-sm inline-flex items-center gap-1"><Square className="w-4 h-4" /> Stop</button>
        )}
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-2">Recent Readings ({history.length})</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between text-[11px] rounded bg-slate-800/60 px-2 py-1">
              <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-rose-400" fill="currentColor" /> <b>{h.bpm}</b> BPM</span>
              <span className="opacity-70 text-[10px]">{h.category}</span>
              <span className="opacity-60 text-[10px]">{new Date(h.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
          {history.length === 0 && <div className="text-xs opacity-60 text-center py-3">No readings yet</div>}
        </div>
      </div>
    </div>
  );
}

// ============ MOTION MATRIX (DeviceMotion API) ============
function MotionMatrixTab() {
  const [tracking, setTracking] = useState(false);
  const [steps, setSteps] = useState(0);
  const [duration, setDuration] = useState(0);
  const [history, setHistory] = useState([]);
  const stateRef = useRef({ last: 0, threshold: 1.2, startTime: 0, timer: null, listener: null });

  useEffect(() => {
    api.get('/health/motion/history', { params: { limit: 20 } }).then((r) => setHistory(r.data?.logs || [])).catch(() => {});
  }, []);

  const start = async () => {
    if (typeof DeviceMotionEvent === 'undefined') { toast.error('DeviceMotion not supported'); return; }
    // iOS permission
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try { const p = await DeviceMotionEvent.requestPermission(); if (p !== 'granted') { toast.error('Motion permission denied'); return; } }
      catch { /* ignore */ }
    }
    stateRef.current.startTime = Date.now();
    setSteps(0); setDuration(0);
    const listener = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      const diff = Math.abs(mag - stateRef.current.last);
      stateRef.current.last = mag;
      if (diff > stateRef.current.threshold) {
        setSteps((s) => s + 1);
      }
    };
    window.addEventListener('devicemotion', listener);
    stateRef.current.listener = listener;
    stateRef.current.timer = setInterval(() => {
      setDuration(Math.floor((Date.now() - stateRef.current.startTime) / 1000));
    }, 1000);
    setTracking(true);
  };

  const stop = async () => {
    window.removeEventListener('devicemotion', stateRef.current.listener);
    clearInterval(stateRef.current.timer);
    setTracking(false);
    if (steps > 0) {
      const distance = steps * 0.75;
      const durSec = Math.floor((Date.now() - stateRef.current.startTime) / 1000);
      try {
        const r = await api.post('/health/motion/log', { steps, distance_m: distance, duration_s: durSec, avg_speed_ms: durSec > 0 ? distance / durSec : 0 });
        toast.success(`${steps} steps saved (~${r.data.log.calories_est} kcal)`);
        setHistory((h) => [r.data.log, ...h].slice(0, 20));
      } catch (e) { toast.error('Save failed'); }
    }
  };

  const km = (steps * 0.75) / 1000;
  return (
    <div className="space-y-3" data-testid="ht-motion-tab">
      <div className="rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-950/60 to-slate-900 p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2 flex items-center gap-1"><Activity className="w-3 h-3" /> Motion Matrix · DeviceMotion API</div>
        <div className="grid grid-cols-3 gap-2 text-center my-4">
          <div>
            <div className="text-[9px] uppercase opacity-70">Steps</div>
            <div className="text-3xl font-black text-emerald-300">{steps}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase opacity-70">Distance</div>
            <div className="text-3xl font-black text-emerald-300">{km.toFixed(2)}<span className="text-sm"> km</span></div>
          </div>
          <div>
            <div className="text-[9px] uppercase opacity-70">Duration</div>
            <div className="text-3xl font-black text-emerald-300">{Math.floor(duration / 60)}:{String(duration % 60).padStart(2, '0')}</div>
          </div>
        </div>
        {!tracking ? (
          <button onClick={start} data-testid="ht-motion-start" className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-black text-sm inline-flex items-center justify-center gap-1"><Play className="w-4 h-4" /> Start Tracking</button>
        ) : (
          <button onClick={stop} data-testid="ht-motion-stop" className="w-full px-4 py-2 rounded-lg bg-red-500 text-white font-black text-sm inline-flex items-center justify-center gap-1"><Square className="w-4 h-4" /> Stop & Save</button>
        )}
        <div className="text-[10px] opacity-70 text-center mt-2">📱 Phone jeb me ya haath me — walking pattern detect hoga</div>
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">Recent Sessions</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between text-[11px] rounded bg-slate-800/60 px-2 py-1">
              <span><b>{h.steps}</b> steps · {((h.distance_m || 0) / 1000).toFixed(2)}km</span>
              <span className="opacity-70 text-[10px]">🔥 {h.calories_est} kcal</span>
              <span className="opacity-60 text-[10px]">{new Date(h.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
          {history.length === 0 && <div className="text-xs opacity-60 text-center py-3">No sessions yet</div>}
        </div>
      </div>
    </div>
  );
}

// ============ FOOD AI SCANNER ============
function FoodScannerTab({ active }) {
  const [imgUrl, setImgUrl] = useState('');
  const [meal, setMeal] = useState('meal');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get('/health/food/history', { params: { limit: 10 } }).then((r) => setHistory(r.data?.logs || [])).catch(() => {});
  }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImageFile(f);
      setImgUrl(url);
      toast.success('Uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };

  const scan = async () => {
    if (!imgUrl) { toast.error('Photo upload karo pehle'); return; }
    if (!active) { toast.error('Active subscription required'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/health/food/scan', { image_url: imgUrl, meal_type: meal });
      setResult(r.data?.scan?.analysis);
      toast.success(`🥗 ${r.data?.scan?.total_calories_kcal || '?'} kcal detected`);
      setHistory((h) => [r.data.scan, ...h].slice(0, 10));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Scan failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="ht-food-tab">
      <div className="rounded-2xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950/50 to-slate-900 p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2 flex items-center gap-1"><Utensils className="w-3 h-3" /> AI Food & Calorie Scanner</div>
        {!active && <div className="text-[10px] text-amber-300 mb-2">⚠️ Active subscription required</div>}
        <div className="flex items-center gap-2 mb-2">
          <label className="flex-1 text-[11px] px-3 py-2 rounded-lg bg-emerald-500 text-black font-black cursor-pointer text-center hover:bg-emerald-400">
            {busy && !result ? '⏳ Uploading…' : `📸 ${imgUrl ? 'Change Photo' : 'Take/Pick Photo'}`}
            <input type="file" accept="image/*" capture="environment" onChange={upload} data-testid="ht-food-upload" className="hidden" />
          </label>
          <select value={meal} onChange={(e) => setMeal(e.target.value)} data-testid="ht-food-meal" className="px-2 py-2 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="meal">🍽️ Meal</option>
            <option value="breakfast">🌅 Breakfast</option>
            <option value="lunch">☀️ Lunch</option>
            <option value="dinner">🌙 Dinner</option>
            <option value="snack">🍿 Snack</option>
          </select>
        </div>
        {imgUrl && <img src={imgUrl} alt="Food" className="w-full max-h-48 object-contain rounded-lg mb-2" />}
        <button onClick={scan} disabled={busy || !imgUrl || !active} data-testid="ht-food-scan" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-sm font-black disabled:opacity-40 flex items-center justify-center gap-1"><Zap className="w-4 h-4" /> {busy ? 'Analyzing…' : 'Scan with AI'}</button>

        {result && (
          <div className="mt-3 rounded-lg bg-slate-800/70 p-3" data-testid="ht-food-result">
            <div className="grid grid-cols-4 gap-2 mb-2 text-center">
              <MacroTile label="Cal" value={result.total_calories_kcal || 0} unit="kcal" color="rose" />
              <MacroTile label="Protein" value={result.total_protein_g || 0} unit="g" color="blue" />
              <MacroTile label="Carbs" value={result.total_carbs_g || 0} unit="g" color="amber" />
              <MacroTile label="Fat" value={result.total_fat_g || 0} unit="g" color="fuchsia" />
            </div>
            {result.health_score !== undefined && (
              <div className="mb-2">
                <div className="text-[10px] opacity-80 flex justify-between"><span>Health Score</span><span className="font-black text-emerald-300">{result.health_score}/100</span></div>
                <div className="h-2 rounded-full bg-slate-700 overflow-hidden"><div className="h-full bg-gradient-to-r from-rose-500 via-amber-500 to-emerald-500" style={{ width: `${result.health_score}%` }}></div></div>
              </div>
            )}
            {result.notes && <div className="text-[11px] opacity-90 italic">💡 {result.notes}</div>}
            {result.foods?.length > 0 && (
              <div className="mt-2 space-y-1">
                {result.foods.map((f, i) => (
                  <div key={i} className="text-[10px] flex justify-between opacity-80"><span>• {f.name} ({f.portion})</span><span className="font-mono">{f.calories_kcal} kcal</span></div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">Recent Scans</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {history.map((h) => (
            <div key={h.id} className="flex items-center gap-2 rounded bg-slate-800/60 p-1.5 text-[11px]">
              <img src={h.image_url} alt="" className="w-10 h-10 rounded object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />
              <div className="flex-1 min-w-0"><div className="font-black truncate">{h.meal_type} · {h.total_calories_kcal || '?'} kcal</div><div className="opacity-70 text-[10px]">{new Date(h.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div></div>
              <span className="text-[10px] text-emerald-300 font-black">{h.health_score ?? '—'}/100</span>
            </div>
          ))}
          {history.length === 0 && <div className="text-xs opacity-60 text-center py-3">No scans yet</div>}
        </div>
      </div>
    </div>
  );
}

const MacroTile = ({ label, value, unit, color }) => {
  const map = { rose: 'text-rose-300', blue: 'text-blue-300', amber: 'text-amber-300', fuchsia: 'text-fuchsia-300' };
  return (
    <div className="rounded bg-slate-900/70 p-2">
      <div className="text-[9px] opacity-70">{label}</div>
      <div className={`text-sm font-black ${map[color]}`}>{Math.round(value)}<span className="text-[9px] opacity-70">{unit}</span></div>
    </div>
  );
};

// ============ BMI PORTAL ============
function BMIPortalTab({ cfg, active }) {
  const [w, setW] = useState(cfg.user?.profile?.weight_kg || '');
  const [h, setH] = useState(cfg.user?.profile?.height_cm || '');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/health/bmi/history', { params: { limit: 10 } }).then((r) => setHistory(r.data?.logs || [])).catch(() => {});
  }, []);
  const calc = async () => {
    if (!w || !h) { toast.error('Weight + Height required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/health/bmi/calc', { weight_kg: parseFloat(w), height_cm: parseFloat(h) });
      setResult(r.data.result);
      setHistory((prev) => [r.data.result, ...prev].slice(0, 10));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  if (!active) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-amber-400/50 bg-amber-500/5 p-6 text-center" data-testid="ht-bmi-locked">
        <Scale className="w-12 h-12 mx-auto opacity-40 mb-2" />
        <div className="text-sm font-black opacity-80">🔒 BMI Portal — Subscribers Only</div>
        <div className="text-[11px] opacity-70 mt-1">Subscribe upar wale banner se · 500 AT/month</div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="ht-bmi-tab">
      <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/50 to-slate-900 p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2 flex items-center gap-1"><Scale className="w-3 h-3" /> BMI Calculator</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input type="number" placeholder="Weight (kg)" value={w} onChange={(e) => setW(e.target.value)} data-testid="ht-bmi-weight" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          <input type="number" placeholder="Height (cm)" value={h} onChange={(e) => setH(e.target.value)} data-testid="ht-bmi-height" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        </div>
        <button onClick={calc} disabled={busy} data-testid="ht-bmi-calc" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-sm font-black disabled:opacity-40">{busy ? 'Calculating…' : 'Calculate BMI'}</button>

        {result && (
          <div className="mt-3 rounded-lg p-3 text-center" style={{ background: result.color + '22', border: `2px solid ${result.color}` }} data-testid="ht-bmi-result">
            <div className="text-[10px] uppercase opacity-80">Your BMI</div>
            <div className="text-4xl font-black" style={{ color: result.color }}>{result.bmi}</div>
            <div className="text-sm font-black" style={{ color: result.color }}>{result.category}</div>
            <div className="text-[11px] opacity-90 mt-1 italic">💡 {result.advice}</div>
          </div>
        )}
      </div>

      {/* Ready Chart */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">📊 BMI Ready Reference Chart</div>
        <div className="space-y-1">
          {cfg.bmi_chart.map((c) => (
            <div key={c.category} className="flex items-center gap-2 rounded p-1.5" style={{ background: c.color + '15', border: `1px solid ${c.color}44` }}>
              <div className="w-3 h-3 rounded-full" style={{ background: c.color }}></div>
              <div className="flex-1 text-[11px] font-black" style={{ color: c.color }}>{c.category}</div>
              <div className="text-[10px] opacity-80 font-mono">{c.min}–{c.max === 200 ? '∞' : c.max}</div>
            </div>
          ))}
        </div>
      </div>

      {history.length > 0 && (
        <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">History</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-[11px] rounded bg-slate-800/60 px-2 py-1">
                <span>W: {h.weight_kg}kg · H: {h.height_cm}cm</span>
                <span className="font-black" style={{ color: h.color }}>BMI {h.bmi} · {h.category}</span>
                <span className="opacity-60 text-[10px]">{new Date(h.created_at).toLocaleDateString('en-IN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MEDICAL LOCKER (OCR) ============
function MedicalLockerTab({ active }) {
  const [docs, setDocs] = useState([]);
  const [imgUrl, setImgUrl] = useState('');
  const [docType, setDocType] = useState('prescription');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get('/health/locker').then((r) => setDocs(r.data?.documents || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const url = await uploadImageFile(f); setImgUrl(url); toast.success('Uploaded'); }
    catch { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };
  const scan = async () => {
    if (!imgUrl) { toast.error('Document upload karo pehle'); return; }
    if (!active) { toast.error('Active subscription required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/health/ocr/scan', { image_url: imgUrl, doc_type: docType, title: title || undefined });
      toast.success('Document saved to locker');
      setImgUrl(''); setTitle('');
      load();
      setExpanded(r.data.document.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'OCR failed'); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete document?')) return;
    try { await api.delete(`/health/locker/${id}`); load(); } catch { toast.error('Failed'); }
  };
  if (!active) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-blue-400/50 bg-blue-500/5 p-6 text-center" data-testid="ht-locker-locked">
        <FolderLock className="w-12 h-12 mx-auto opacity-40 mb-2" />
        <div className="text-sm font-black opacity-80">🔒 Medical Locker — Subscribers Only</div>
        <div className="text-[11px] opacity-70 mt-1">Prescriptions, lab reports · AI OCR · Encrypted store</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="ht-locker-tab">
      <div className="rounded-2xl border-2 border-blue-400/40 bg-gradient-to-br from-blue-950/50 to-slate-900 p-4">
        <div className="text-[10px] uppercase tracking-widest text-blue-300 font-black mb-2 flex items-center gap-1"><FolderLock className="w-3 h-3" /> Smart Medical OCR + Locker</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="ht-ocr-type" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="prescription">💊 Prescription</option>
            <option value="lab_report">🧪 Lab Report</option>
            <option value="discharge">🏥 Discharge Summary</option>
            <option value="bill">💰 Medical Bill</option>
            <option value="other">📄 Other</option>
          </select>
          <input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ht-ocr-title" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <label className="block text-[11px] px-3 py-2 rounded-lg bg-blue-500 text-white font-black cursor-pointer text-center mb-2 hover:bg-blue-400">
          {busy && !imgUrl ? '⏳ Uploading…' : `📸 ${imgUrl ? 'Change Document' : 'Upload/Photo Document'}`}
          <input type="file" accept="image/*" capture="environment" onChange={upload} data-testid="ht-ocr-upload" className="hidden" />
        </label>
        {imgUrl && <img src={imgUrl} alt="Doc" className="w-full max-h-40 object-contain rounded-lg mb-2" />}
        <button onClick={scan} disabled={busy || !imgUrl} data-testid="ht-ocr-scan" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-black disabled:opacity-40 flex items-center justify-center gap-1"><Zap className="w-4 h-4" /> {busy ? 'AI reading…' : 'Scan & Save to Locker'}</button>
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-blue-300 font-black mb-2">📁 My Locker ({docs.length})</div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {docs.map((d) => (
            <div key={d.id} className="rounded-lg border border-slate-700 bg-slate-800/50" data-testid={`ht-doc-${d.id}`}>
              <div className="p-2 flex items-center gap-2">
                <img src={d.image_url} alt="" className="w-12 h-12 rounded object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black truncate">{d.title}</div>
                  <div className="text-[10px] opacity-70">{d.doc_type} · {new Date(d.created_at).toLocaleDateString('en-IN')}</div>
                </div>
                <button onClick={() => setExpanded(expanded === d.id ? null : d.id)} className="text-[10px] px-2 py-0.5 rounded bg-blue-500/25 text-blue-200 font-black">{expanded === d.id ? '▲' : '▼'}</button>
                <button onClick={() => del(d.id)} className="text-red-300 text-xs">✕</button>
              </div>
              {expanded === d.id && (
                <pre className="text-[9px] bg-slate-950 p-2 max-h-64 overflow-auto whitespace-pre-wrap border-t border-slate-700 rounded-b-lg">{JSON.stringify(d.extracted, null, 2)}</pre>
              )}
            </div>
          ))}
          {docs.length === 0 && <div className="text-xs opacity-60 text-center py-3">Locker empty</div>}
        </div>
      </div>
    </div>
  );
}

// Stable field renderer (hoisted so parent re-renders don't remount inputs → cursor stays put)
const DailyF = ({ k, l, unit, type = 'number', step, placeholder, form, setField }) => (
  <label className="flex flex-col text-[10px] opacity-90">
    <span className="mb-0.5">{l}{unit ? ` (${unit})` : ''}</span>
    <input type={type} step={step} placeholder={placeholder} value={form[k] ?? ''} onChange={(e) => setField(k, e.target.value)} data-testid={`ht-daily-${k}`} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
  </label>
);

// ============ DAILY LOG TAB — date-based multi-metric with editable + PDF report ============
function DailyLogTab({ active }) {
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr());
  const [form, setForm] = useState({});
  const [customPairs, setCustomPairs] = useState([]); // [{k,v}]
  const [customK, setCustomK] = useState(''); const [customV, setCustomV] = useState('');
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reportRange, setReportRange] = useState({ from: '', to: '' });
  const [expandedId, setExpandedId] = useState(null);

  const loadLogs = useCallback(async () => {
    try {
      const r = await api.get('/health/daily/logs', { params: { limit: 60 } });
      setLogs(r.data?.logs || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  // When date changes, pre-fill from existing log for that date
  useEffect(() => {
    const existing = logs.find((l) => l.date === date);
    if (existing) {
      const { user_id, id, created_at, updated_at, custom, date: _d, ...rest } = existing;
      setForm(rest);
      setCustomPairs(custom ? Object.entries(custom).map(([k, v]) => ({ k, v: String(v) })) : []);
    } else {
      setForm({});
      setCustomPairs([]);
    }
  }, [date, logs]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v === '' ? null : v }));

  const addCustom = () => {
    if (!customK.trim()) return;
    setCustomPairs([...customPairs, { k: customK.trim(), v: customV.trim() }]);
    setCustomK(''); setCustomV('');
  };
  const removeCustom = (i) => setCustomPairs(customPairs.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!active) { toast.error('Subscribe pehle to unlock daily logs'); return; }
    setBusy(true);
    try {
      const payload = { date, ...form };
      const num = (v) => v === '' || v === null || v === undefined ? null : (isNaN(parseFloat(v)) ? null : parseFloat(v));
      const intN = (v) => v === '' || v === null || v === undefined ? null : (isNaN(parseInt(v)) ? null : parseInt(v));
      payload.bp_sys = intN(payload.bp_sys);
      payload.bp_dia = intN(payload.bp_dia);
      payload.pulse_bpm = intN(payload.pulse_bpm);
      payload.sugar_fasting = num(payload.sugar_fasting);
      payload.sugar_pp = num(payload.sugar_pp);
      payload.weight_kg = num(payload.weight_kg);
      payload.walk_steps = intN(payload.walk_steps);
      payload.walk_km = num(payload.walk_km);
      payload.water_glasses = intN(payload.water_glasses);
      payload.sleep_hours = num(payload.sleep_hours);
      payload.custom = customPairs.reduce((a, { k, v }) => { if (k) a[k] = v; return a; }, {});
      await api.post('/health/daily/log', payload);
      toast.success('✓ Saved for ' + date);
      loadLogs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Delete this day\'s log?')) return;
    try { await api.delete(`/health/daily/log/${id}`); toast.success('Deleted'); loadLogs(); }
    catch { toast.error('Failed'); }
  };

  const generateReport = async () => {
    const from = reportRange.from || logs[logs.length - 1]?.date;
    const to = reportRange.to || todayStr();
    if (!from || !to) { toast.error('Select date range'); return; }
    setBusy(true);
    try {
      const r = await api.get('/health/daily/report', { params: { from_date: from, to_date: to } });
      const { summary, logs: rows } = r.data;
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      doc.setFillColor(6, 78, 59); doc.rect(0, 0, 595, 80, 'F');
      doc.setTextColor(240, 253, 244); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
      doc.text('ANANT HEALTH TRACKER · Daily Report', 297, 40, { align: 'center' });
      doc.setFontSize(11); doc.text(`${from}  →  ${to}   ·   ${summary.days_logged} days logged`, 297, 60, { align: 'center' });
      let y = 110;
      doc.setTextColor(20); doc.setFontSize(13); doc.setFont('helvetica', 'bold');
      doc.text('📊 Summary Averages', 40, y); y += 22;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
      const rowSum = (label, val, unit = '') => { if (val === null || val === undefined) return; doc.text(`${label}: ${val}${unit}`, 60, y); y += 16; };
      rowSum('Avg BP', summary.avg_bp_sys && summary.avg_bp_dia ? `${summary.avg_bp_sys}/${summary.avg_bp_dia}` : null, ' mmHg');
      rowSum('Avg Pulse', summary.avg_pulse, ' bpm');
      rowSum('Avg Sugar Fasting', summary.avg_sugar_fasting, ' mg/dL');
      rowSum('Avg Sugar PP', summary.avg_sugar_pp, ' mg/dL');
      rowSum('Avg Weight', summary.avg_weight_kg, ' kg');
      rowSum('Total Steps', summary.total_walk_steps || 0);
      rowSum('Total Distance', summary.total_walk_km, ' km');
      rowSum('Avg Sleep', summary.avg_sleep_hours, ' hr');
      rowSum('Avg Water', summary.avg_water_glasses, ' glasses');
      y += 10;
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.text('📅 Daily Log Entries', 40, y); y += 20;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
      rows.forEach((r) => {
        if (y > 780) { doc.addPage(); y = 50; }
        doc.setFont('helvetica', 'bold'); doc.text(r.date, 40, y);
        doc.setFont('helvetica', 'normal');
        const parts = [];
        if (r.bp_sys && r.bp_dia) parts.push(`BP ${r.bp_sys}/${r.bp_dia}`);
        if (r.pulse_bpm) parts.push(`Pulse ${r.pulse_bpm}`);
        if (r.sugar_fasting) parts.push(`Sugar F ${r.sugar_fasting}`);
        if (r.sugar_pp) parts.push(`Sugar PP ${r.sugar_pp}`);
        if (r.weight_kg) parts.push(`Wt ${r.weight_kg}kg`);
        if (r.walk_steps) parts.push(`${r.walk_steps} steps`);
        if (r.sleep_hours) parts.push(`Sleep ${r.sleep_hours}h`);
        if (r.mood) parts.push(`Mood: ${r.mood}`);
        doc.text(parts.join(' · '), 100, y, { maxWidth: 460 });
        y += 14;
        if (r.diet_notes) { doc.setTextColor(80); doc.text(`Diet: ${r.diet_notes}`.slice(0, 180), 100, y, { maxWidth: 460 }); doc.setTextColor(20); y += 14; }
        if (r.meds_taken) { doc.setTextColor(80); doc.text(`Meds: ${r.meds_taken}`.slice(0, 180), 100, y, { maxWidth: 460 }); doc.setTextColor(20); y += 14; }
        if (r.symptoms) { doc.setTextColor(160, 30, 30); doc.text(`Symptoms: ${r.symptoms}`.slice(0, 180), 100, y, { maxWidth: 460 }); doc.setTextColor(20); y += 14; }
        if (r.custom && Object.keys(r.custom).length) {
          doc.setTextColor(80);
          doc.text('Custom: ' + Object.entries(r.custom).map(([k, v]) => `${k}=${v}`).join(', '), 100, y, { maxWidth: 460 });
          doc.setTextColor(20); y += 14;
        }
        y += 6;
      });
      doc.setFontSize(8); doc.setTextColor(120);
      doc.text('This report is for informational purposes only. Consult a doctor for medical decisions.', 40, 820);
      doc.save(`health-report-${from}-to-${to}.pdf`);
      toast.success('✓ Report downloaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Report failed'); }
    finally { setBusy(false); }
  };

  // NOTE: DailyF is hoisted outside so parent re-renders (from onChange) don't remount inputs — cursor stays where user typed.

  return (
    <div className="space-y-3" data-testid="ht-daily-tab">
      <div className="rounded-2xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950/40 to-slate-900 p-3">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-emerald-300 font-black flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Daily Health Log · Multi-Metric</div>
          <div className="flex items-center gap-2">
            <VoiceInputHelper label="Daily Voice"
              onDictate={(text) => {
                const parsed = parseNumericFields(text, {
                  bp_sys_dia: ['bp', 'blood pressure', 'ब्लड प्रेशर', 'ब्लडप्रेशर'],
                  sugar_fasting: ['sugar fasting', 'fasting sugar', 'शुगर फास्टिंग', 'shugar fasting'],
                  sugar_pp: ['sugar pp', 'pp sugar', 'shugar pp'],
                  pulse_bpm: ['pulse', 'नाड़ी', 'nadi', 'bpm'],
                  weight_kg: ['weight', 'wajan', 'वजन', 'kg'],
                  walk_steps: ['steps', 'kadam', 'कदम'],
                  walk_km: ['distance', 'km', 'kilometer', 'किलोमीटर'],
                  water_glasses: ['water', 'pani', 'पानी', 'glasses', 'glass'],
                  sleep_hours: ['sleep', 'neend', 'नींद', 'hours'],
                });
                const patch = {};
                if (parsed.bp_sys_dia) {
                  const parts = parsed.bp_sys_dia.split(/\s*(?:by|slash|\/|बटा|पर)\s*/i);
                  if (parts[0]) patch.bp_sys = parts[0];
                  if (parts[1]) patch.bp_dia = parts[1];
                }
                if (parsed.sugar_fasting) patch.sugar_fasting = parsed.sugar_fasting;
                if (parsed.sugar_pp) patch.sugar_pp = parsed.sugar_pp;
                if (parsed.pulse_bpm) patch.pulse_bpm = parsed.pulse_bpm;
                if (parsed.weight_kg) patch.weight_kg = parsed.weight_kg;
                if (parsed.walk_steps) patch.walk_steps = parsed.walk_steps;
                if (parsed.walk_km) patch.walk_km = parsed.walk_km;
                if (parsed.water_glasses) patch.water_glasses = parsed.water_glasses;
                if (parsed.sleep_hours) patch.sleep_hours = parsed.sleep_hours;
                if (Object.keys(patch).length) { setForm((f) => ({ ...f, ...patch })); toast.success(`🎤 Filled: ${Object.keys(patch).join(', ')}`); }
                else toast.error('🎤 Say: "BP 120 by 80, sugar fasting 95, weight 68, steps 5000"');
              }}
            />
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ht-daily-date" max={todayStr()} className="px-2 py-1.5 rounded bg-slate-800 border border-emerald-400/50 text-xs font-black" />
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-1">🩺 Vitals</div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <DailyF k="bp_sys" l="BP Systolic" unit="mmHg" placeholder="120" form={form} setField={setField} />
          <DailyF k="bp_dia" l="BP Diastolic" unit="mmHg" placeholder="80" form={form} setField={setField} />
          <DailyF k="pulse_bpm" l="Pulse" unit="bpm" placeholder="72" form={form} setField={setField} />
          <DailyF k="sugar_fasting" l="Sugar Fasting" unit="mg/dL" step="0.1" placeholder="95" form={form} setField={setField} />
          <DailyF k="sugar_pp" l="Sugar PP" unit="mg/dL" step="0.1" placeholder="140" form={form} setField={setField} />
          <DailyF k="weight_kg" l="Weight" unit="kg" step="0.1" placeholder="70" form={form} setField={setField} />
        </div>

        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-1">🚶 Activity & Rest</div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <DailyF k="walk_steps" l="Steps" placeholder="6000" form={form} setField={setField} />
          <DailyF k="walk_km" l="Distance" unit="km" step="0.1" placeholder="4.5" form={form} setField={setField} />
          <DailyF k="water_glasses" l="Water" unit="glasses" placeholder="8" form={form} setField={setField} />
          <DailyF k="sleep_hours" l="Sleep" unit="hours" step="0.1" placeholder="7" form={form} setField={setField} />
          <label className="flex flex-col text-[10px] opacity-90 col-span-2">
            <span className="mb-0.5">😊 Mood</span>
            <select value={form.mood || ''} onChange={(e) => setField('mood', e.target.value)} data-testid="ht-daily-mood" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
              <option value="">—</option>
              <option>Happy</option><option>Energetic</option><option>Neutral</option><option>Tired</option><option>Anxious</option><option>Sad</option>
            </select>
          </label>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-1">🍽️ Notes</div>
        <div className="space-y-2 mb-3">
          <textarea rows={2} placeholder="Diet notes (breakfast/lunch/dinner)" value={form.diet_notes || ''} onChange={(e) => setField('diet_notes', e.target.value)} data-testid="ht-daily-diet" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <textarea rows={2} placeholder="Medicines taken today" value={form.meds_taken || ''} onChange={(e) => setField('meds_taken', e.target.value)} data-testid="ht-daily-meds" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <textarea rows={2} placeholder="Symptoms / observations (if any)" value={form.symptoms || ''} onChange={(e) => setField('symptoms', e.target.value)} data-testid="ht-daily-symp" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>

        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-1">➕ Custom Metrics</div>
        <div className="flex flex-wrap gap-1 mb-2">
          {customPairs.map((p, i) => (
            <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-fuchsia-500/25 border border-fuchsia-400/40 flex items-center gap-1" data-testid={`ht-custom-${i}`}>
              <b>{p.k}:</b> {p.v}
              <button onClick={() => removeCustom(i)} className="ml-1 text-rose-300">✕</button>
            </span>
          ))}
        </div>
        <div className="flex gap-1 mb-3">
          <input placeholder="Metric name (e.g. Oxygen SpO2)" value={customK} onChange={(e) => setCustomK(e.target.value)} data-testid="ht-custom-k" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Value (e.g. 98%)" value={customV} onChange={(e) => setCustomV(e.target.value)} data-testid="ht-custom-v" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={addCustom} data-testid="ht-custom-add" className="px-3 rounded bg-fuchsia-500 text-white text-xs font-black"><Plus className="w-3 h-3" /></button>
        </div>

        <button onClick={save} disabled={busy || !active} data-testid="ht-daily-save" className="w-full px-3 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-black text-sm font-black disabled:opacity-40">{busy ? '⏳ Saving…' : `💾 Save for ${date}`}</button>
      </div>

      {/* Report generator */}
      <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 to-slate-900 p-3" data-testid="ht-daily-report-card">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2 flex items-center gap-1"><FileDown className="w-3 h-3" /> Generate PDF Health Report</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <label className="flex flex-col text-[10px] opacity-90">
            <span className="mb-0.5">From</span>
            <input type="date" value={reportRange.from} onChange={(e) => setReportRange({ ...reportRange, from: e.target.value })} data-testid="ht-report-from" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
          <label className="flex flex-col text-[10px] opacity-90">
            <span className="mb-0.5">To</span>
            <input type="date" value={reportRange.to} onChange={(e) => setReportRange({ ...reportRange, to: e.target.value })} data-testid="ht-report-to" max={todayStr()} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
        </div>
        <button onClick={generateReport} disabled={busy} data-testid="ht-report-gen" className="w-full px-3 py-2 rounded bg-amber-500 text-black text-xs font-black disabled:opacity-40">📄 Download Full Report PDF</button>
      </div>

      {/* Existing logs */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-300 font-black mb-2">📅 Recent Logs · {logs.length}</div>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {logs.map((l) => (
            <div key={l.id} className="rounded bg-slate-800/60 p-2 text-[11px]" data-testid={`ht-log-${l.id}`}>
              <div className="flex items-center gap-2">
                <b className="flex-1">{l.date}</b>
                <button onClick={() => setDate(l.date)} data-testid={`ht-log-edit-${l.id}`} className="text-[9px] px-2 py-0.5 rounded bg-amber-500/25 text-amber-200 font-black"><Edit3 className="w-2.5 h-2.5 inline" /> Edit</button>
                <button onClick={() => setExpandedId(expandedId === l.id ? null : l.id)} className="text-[9px] px-2 py-0.5 rounded bg-slate-700 text-slate-200 font-black">{expandedId === l.id ? '▲' : '▼'}</button>
                <button onClick={() => deleteLog(l.id)} className="text-[9px] px-2 py-0.5 rounded bg-red-500/30 text-red-300"><Trash2 className="w-2.5 h-2.5 inline" /></button>
              </div>
              <div className="text-[10px] opacity-80 flex flex-wrap gap-2 mt-0.5">
                {l.bp_sys && l.bp_dia && <span>🩺 BP {l.bp_sys}/{l.bp_dia}</span>}
                {l.pulse_bpm && <span>❤️ {l.pulse_bpm} bpm</span>}
                {l.sugar_fasting && <span>🩸 F {l.sugar_fasting}</span>}
                {l.sugar_pp && <span>🩸 PP {l.sugar_pp}</span>}
                {l.weight_kg && <span>⚖️ {l.weight_kg}kg</span>}
                {l.walk_steps && <span>🚶 {l.walk_steps}</span>}
                {l.sleep_hours && <span>😴 {l.sleep_hours}h</span>}
                {l.mood && <span>😊 {l.mood}</span>}
              </div>
              {expandedId === l.id && (
                <div className="mt-1 text-[10px] space-y-0.5 opacity-90 border-t border-slate-700 pt-1">
                  {l.diet_notes && <div>🍽️ <b>Diet:</b> {l.diet_notes}</div>}
                  {l.meds_taken && <div>💊 <b>Meds:</b> {l.meds_taken}</div>}
                  {l.symptoms && <div className="text-rose-300">⚠️ <b>Symptoms:</b> {l.symptoms}</div>}
                  {l.custom && Object.keys(l.custom).length > 0 && <div>➕ {Object.entries(l.custom).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>}
                </div>
              )}
            </div>
          ))}
          {logs.length === 0 && <div className="text-center text-xs opacity-60 py-6">No logs yet · Fill the form above and hit Save</div>}
        </div>
      </div>
    </div>
  );
}


// ============ AI HEALTH REPORT TAB (Claude Sonnet · 30/month cap) ============
function AIReportTab({ active }) {
  const [quota, setQuota] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [periodDays, setPeriodDays] = useState(30);
  const [focus, setFocus] = useState('');

  const loadQuota = async () => {
    try { const r = await api.get('/health/ai-report/quota'); setQuota(r.data); } catch {}
  };
  const loadHistory = async () => {
    try { const r = await api.get('/health/ai-report/history'); setHistory(r.data?.reports || []); } catch {}
  };
  useEffect(() => { loadQuota(); loadHistory(); }, []);

  const generate = async () => {
    if (!active) { toast.error('Active plan required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/health/ai-report/generate', { period_days: periodDays, focus });
      setSelected(r.data?.report);
      toast.success(`✅ Report generated (${r.data?.remaining_this_month}/30 remaining)`);
      loadQuota(); loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Report failed');
    } finally { setBusy(false); }
  };

  const download = () => {
    if (!selected) return;
    const html = renderReportHTML(selected);
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked'); return; }
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const share = () => {
    if (!selected) return;
    const r = selected.report || {};
    const txt = `🩺 *${r.title || 'Health Report'}*\n${r.overall_verdict || ''} · Score: ${r.overall_score || '—'}/100\n\n${(r.key_findings || []).slice(0, 3).map((f) => `• ${f}`).join('\n')}\n\n_Generated by Anant Health Tracker_`;
    const url = `https://wa.me/?text=${encodeURIComponent(txt)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-3">
      {!active && <div className="p-3 rounded-xl bg-amber-900/40 border border-amber-500/50 text-xs">🔒 AI Health Report needs active plan.</div>}
      <div className="p-3 rounded-2xl bg-slate-900 border border-emerald-400/30 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-black text-emerald-300">🩺 Generate AI Health Report</div>
          {quota && <div className="text-[10px] opacity-70">Monthly: {quota.used}/{quota.limit} · {quota.remaining} left</div>}
        </div>
        <div className="flex gap-2">
          <select value={periodDays} onChange={(e) => setPeriodDays(parseInt(e.target.value))} data-testid="ht-ai-period"
                  className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs">
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus (e.g. sugar, BP)"
                 data-testid="ht-ai-focus" className="flex-1 px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <button onClick={generate} disabled={busy || !active || (quota && quota.remaining <= 0)} data-testid="ht-ai-generate"
                className="w-full py-2 rounded-xl bg-gradient-to-r from-rose-500 via-fuchsia-500 to-emerald-500 text-white font-black text-sm disabled:opacity-40 flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate Report</>}
        </button>
      </div>

      {selected && !compareMode && <ReportViewer report={selected} onDownload={download} onShare={share} />}
      {compareMode && history.length >= 2 && <ReportCompareView reports={history.slice(0, 2)} onClose={() => setCompareMode(false)} />}

      {history.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Report History</div>
            {history.length >= 2 && (
              <button onClick={() => { setCompareMode(!compareMode); setSelected(null); }}
                      data-testid="ht-ai-compare-btn"
                      className={`text-[10px] px-2 py-1 rounded-lg font-black flex items-center gap-1 ${compareMode ? 'bg-amber-500 text-slate-900' : 'bg-cyan-600 text-white'}`}>
                {compareMode ? '✕ Close Compare' : '📊 Compare Last 2'}
              </button>
            )}
          </div>
          {history.map((h) => (
            <button key={h.id} onClick={() => { setSelected(h); setCompareMode(false); }} data-testid={`ht-ai-history-${h.id}`}
                    className="w-full text-left p-2.5 rounded-xl bg-slate-900 border border-slate-800 hover:border-emerald-400/50">
              <div className="text-xs font-black">{h.report?.title || `Report · ${h.created_at?.slice(0, 10)}`}</div>
              <div className="text-[10px] opacity-70">{h.period_days}d · Score {h.report?.overall_score || '—'} · {new Date(h.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ REPORT COMPARE VIEW (side-by-side of last 2 reports) ============
function ReportCompareView({ reports, onClose }) {
  const [newer, older] = reports;  // history is newest-first
  const nR = newer.report || {};
  const oR = older.report || {};

  const scoreDiff = (nR.overall_score || 0) - (oR.overall_score || 0);
  const scoreArrow = scoreDiff > 0 ? '📈' : scoreDiff < 0 ? '📉' : '➖';
  const scoreColor = scoreDiff > 0 ? 'text-emerald-300' : scoreDiff < 0 ? 'text-rose-300' : 'text-slate-300';

  const dateOf = (r) => new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-cyan-400/40 overflow-hidden" data-testid="ht-ai-compare-view">
      <div className="p-3 bg-cyan-900/40 flex items-center justify-between">
        <div>
          <div className="text-xs font-black text-cyan-200">📊 Report Compare</div>
          <div className="text-[10px] opacity-70">{dateOf(older)} → {dateOf(newer)}</div>
        </div>
        <div className={`text-right ${scoreColor}`}>
          <div className="text-2xl font-black">{scoreArrow} {scoreDiff > 0 ? '+' : ''}{scoreDiff}</div>
          <div className="text-[10px] opacity-70">Score change</div>
        </div>
      </div>
      <div className="grid grid-cols-2 divide-x divide-slate-800">
        <CompareColumn r={oR} label={`OLDER · ${dateOf(older)}`} highlight="rose" />
        <CompareColumn r={nR} label={`NEWER · ${dateOf(newer)}`} highlight="emerald" />
      </div>
      <div className="p-3 border-t border-slate-800 space-y-2">
        <CompareDiffRow label="Overall Verdict" older={oR.overall_verdict} newer={nR.overall_verdict} />
        <CompareDiffRow label="Score" older={`${oR.overall_score || '—'}/100`} newer={`${nR.overall_score || '—'}/100`} />
        {(() => {
          const oldFindings = new Set(oR.key_findings || []);
          const newFindings = new Set(nR.key_findings || []);
          const resolved = [...oldFindings].filter((f) => !newFindings.has(f));
          const fresh = [...newFindings].filter((f) => !oldFindings.has(f));
          return (
            <>
              {resolved.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-emerald-300 mb-1">✅ Resolved</div>
                  <ul className="space-y-0.5 text-xs opacity-90">{resolved.map((f, i) => <li key={i} className="flex gap-1.5"><span>•</span><span className="line-through opacity-70">{f}</span></li>)}</ul>
                </div>
              )}
              {fresh.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-amber-300 mb-1">🆕 New Findings</div>
                  <ul className="space-y-0.5 text-xs opacity-90">{fresh.map((f, i) => <li key={i} className="flex gap-1.5"><span>•</span><span>{f}</span></li>)}</ul>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

function CompareColumn({ r, label, highlight }) {
  const border = highlight === 'emerald' ? 'border-emerald-400/30' : 'border-rose-400/30';
  const text = highlight === 'emerald' ? 'text-emerald-300' : 'text-rose-300';
  return (
    <div className="p-3 space-y-2">
      <div className={`text-[9px] uppercase tracking-widest font-black ${text}`}>{label}</div>
      <div className="flex flex-wrap gap-1">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black bg-slate-800 border ${border}`}>Score: {r.overall_score || '—'}</span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black bg-slate-800 border ${border}`}>{r.overall_verdict || '—'}</span>
      </div>
      <div>
        <div className="text-[10px] font-black opacity-70">Key Findings</div>
        <ul className="text-[11px] opacity-90 space-y-0.5 mt-0.5">
          {(r.key_findings || []).slice(0, 4).map((f, i) => <li key={i} className="flex gap-1"><span>•</span><span>{f}</span></li>)}
        </ul>
      </div>
      {r.diet_chart?.breakfast && (
        <div className="text-[10px] opacity-80">
          <b className="opacity-70">Breakfast:</b> {r.diet_chart.breakfast.slice(0, 60)}
        </div>
      )}
    </div>
  );
}

function CompareDiffRow({ label, older, newer }) {
  const same = older === newer;
  return (
    <div className="flex items-center justify-between text-[11px] p-2 rounded-lg bg-slate-950/40">
      <div className="font-black opacity-70">{label}</div>
      <div className="flex items-center gap-2">
        <span className="text-rose-300">{older || '—'}</span>
        <span className="opacity-40">→</span>
        <span className={same ? 'opacity-60' : 'text-emerald-300 font-black'}>{newer || '—'}</span>
      </div>
    </div>
  );
}

function ReportViewer({ report, onDownload, onShare }) {
  const r = report.report || {};
  return (
    <div className="p-4 rounded-2xl bg-slate-900 border-2 border-emerald-400/40 space-y-3" data-testid="ht-ai-report-view">
      <div className="flex items-center justify-between">
        <div className="text-lg font-black">{r.title || 'Health Report'}</div>
        <div className="flex gap-2">
          <button onClick={onDownload} data-testid="ht-ai-download" className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center gap-1"><Download className="w-3 h-3" /> PDF</button>
          <button onClick={onShare} data-testid="ht-ai-share" className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-black">📱 Share</button>
        </div>
      </div>
      {r.greeting && <div className="text-sm opacity-90 italic">{r.greeting}</div>}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 rounded-full text-xs font-black bg-emerald-500/30 border border-emerald-400/50">Score: {r.overall_score || '—'}/100</span>
        <span className="px-3 py-1 rounded-full text-xs font-black bg-cyan-500/30 border border-cyan-400/50">{r.overall_verdict || '—'}</span>
      </div>

      <ReportSection title="🔍 Key Findings" items={r.key_findings} />
      <ReportSection title="📈 Future Trends" items={r.future_trends} />

      {r.possible_diseases?.length > 0 && (
        <div>
          <div className="text-xs font-black text-rose-300 mb-1">⚠️ Possible Risks</div>
          <div className="space-y-1">
            {r.possible_diseases.map((d, i) => (
              <div key={i} className="p-2 rounded-lg bg-rose-900/20 border border-rose-500/30 text-xs">
                <b>{d.name}</b> <span className="text-[10px] opacity-70">· {d.risk} risk</span>
                <div className="opacity-80">{d.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ReportSection title="💡 Solutions" items={r.solutions} />

      {r.diet_chart && (
        <div>
          <div className="text-xs font-black text-amber-300 mb-1">🍽️ Diet Chart</div>
          <div className="space-y-1 text-xs">
            {['breakfast', 'lunch', 'snack', 'dinner', 'avoid'].map((k) => (
              r.diet_chart[k] && (
                <div key={k} className="flex gap-2">
                  <b className="capitalize w-20 opacity-80">{k}:</b>
                  <span className="flex-1">{r.diet_chart[k]}</span>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      <ReportSection title="🏋️ Exercise Plan" items={r.exercise_plan} />
      <ReportSection title="🌿 Ayurveda Tips" items={r.ayurveda_tips} />

      {r.when_to_see_doctor && (
        <div className="p-3 rounded-xl bg-cyan-900/30 border border-cyan-500/40 text-xs">
          <b>👨‍⚕️ When to see doctor:</b> {r.when_to_see_doctor}
        </div>
      )}
      {r.motivational_closing && (
        <div className="text-sm font-black text-emerald-300 italic text-center pt-2">{r.motivational_closing}</div>
      )}
    </div>
  );
}

function ReportSection({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-xs font-black text-emerald-300 mb-1">{title}</div>
      <ul className="space-y-0.5 text-xs opacity-90">
        {items.map((it, i) => <li key={i} className="flex gap-1.5"><span>•</span><span>{it}</span></li>)}
      </ul>
    </div>
  );
}

function renderReportHTML(rep) {
  const r = rep.report || {};
  const list = (a) => (a || []).map((x) => `<li>${x}</li>`).join('');
  const dz = (a) => (a || []).map((x) => `<li><b>${x.name}</b> (${x.risk}): ${x.reason}</li>`).join('');
  const diet = r.diet_chart || {};
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${r.title || 'Health Report'}</title>
    <style>
      body{font-family:Arial,sans-serif;max-width:800px;margin:20px auto;padding:20px;color:#111;line-height:1.55}
      h1{color:#059669;border-bottom:3px solid #059669;padding-bottom:8px;margin-bottom:8px}
      h2{color:#0891b2;margin-top:20px;font-size:16px}
      .chip{display:inline-block;padding:4px 10px;border-radius:12px;font-size:12px;font-weight:bold;margin-right:6px}
      .score{background:#d1fae5;color:#065f46}
      .verdict{background:#cffafe;color:#164e63}
      .greeting{font-style:italic;color:#475569;margin:8px 0}
      .warn{background:#fee2e2;border-left:4px solid #dc2626;padding:8px;margin:8px 0;border-radius:6px}
      ul{margin:6px 0 12px 20px}
      table{border-collapse:collapse;margin-top:6px;width:100%}
      td{padding:4px 8px;border-bottom:1px solid #e5e7eb;font-size:13px}
      td:first-child{font-weight:bold;text-transform:capitalize;color:#0891b2;width:110px}
      .footer{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:11px;color:#666;text-align:center}
    </style></head><body>
    <h1>${r.title || 'AI Health Report'}</h1>
    <div class="greeting">${r.greeting || ''}</div>
    <div><span class="chip score">Score: ${r.overall_score || '—'}/100</span><span class="chip verdict">${r.overall_verdict || '—'}</span></div>
    <h2>🔍 Key Findings</h2><ul>${list(r.key_findings)}</ul>
    <h2>📈 Future Trends</h2><ul>${list(r.future_trends)}</ul>
    <h2>⚠️ Possible Risks</h2><ul>${dz(r.possible_diseases)}</ul>
    <h2>💡 Solutions</h2><ul>${list(r.solutions)}</ul>
    <h2>🍽️ Diet Chart</h2><table>
      ${['breakfast','lunch','snack','dinner','avoid'].map((k) => diet[k] ? `<tr><td>${k}</td><td>${diet[k]}</td></tr>` : '').join('')}
    </table>
    <h2>🏋️ Exercise Plan</h2><ul>${list(r.exercise_plan)}</ul>
    <h2>🌿 Ayurveda Tips</h2><ul>${list(r.ayurveda_tips)}</ul>
    ${r.when_to_see_doctor ? `<div class="warn"><b>👨‍⚕️ When to see doctor:</b> ${r.when_to_see_doctor}</div>` : ''}
    <p style="text-align:center;font-style:italic;color:#059669;margin-top:20px">${r.motivational_closing || ''}</p>
    <div class="footer">Generated by Anant Health Tracker · ${new Date(rep.created_at).toLocaleString('en-IN')} · Informational only — consult a doctor for medical decisions</div>
    </body></html>`;
}
